import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSuperAdminAuth } from '../contexts/SuperAdminAuthContext';
import { useSuperAdminTheme } from '../contexts/SuperAdminThemeContext';
import { toast } from 'sonner';
import { Shield, Lock, Eye, EyeOff, AlertTriangle, Moon, Sun, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { cn } from '../lib/utils';

/**
 * SuperAdmin Login Page (Governor Only)
 * 
 * USER HIERARCHY:
 * - Governor (role="governor") - CAN access this page
 * - Superadmin (role="superadmin") - CANNOT access this page
 * - User (role="user") - CANNOT access this page
 * 
 * SECURITY REQUIREMENTS:
 * - Isolated from CRM login flow
 * - Only accepts role='governor' credentials (Lockene internal)
 * - Issues governance-scoped tokens
 * - Redirects to /governance/dashboard on success
 * - Uses SuperAdminThemeContext (NOT CRM theme)
 */
export default function SuperAdminLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useSuperAdminAuth();
  const { theme, toggleTheme, isDark } = useSuperAdminTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);

  // Check for session expired query param
  useEffect(() => {
    const expired = searchParams.get('expired') === 'true' || localStorage.getItem('superadmin_session_expired') === 'true';
    if (expired) {
      setSessionExpired(true);
      localStorage.removeItem('superadmin_session_expired');
      toast.error('Your session has expired. Please log in again.', {
        duration: 5000,
      });
    }
  }, [searchParams]);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/governance/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSessionExpired(false);
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.success) {
        toast.success('Access granted');
        navigate('/governance/dashboard', { replace: true });
      } else {
        setError(result.error || 'Access denied');
        toast.error(result.error || 'Access denied');
      }
    } catch (err) {
      setError('Authentication failed');
      toast.error('Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className={cn(
        "min-h-screen flex items-center justify-center p-4",
        isDark ? "bg-slate-950" : "bg-slate-100"
      )} 
      data-testid="superadmin-login"
      data-theme={theme}
    >
      {/* Security background pattern - only in dark mode */}
      {isDark && (
        <>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzFmMjkzNyIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>
        </>
      )}

      {/* Theme toggle in top right corner */}
      <div className="absolute top-4 right-4 z-20">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className={cn(
            'h-10 w-10 rounded-lg',
            isDark 
              ? 'hover:bg-slate-800 text-slate-400 hover:text-white' 
              : 'hover:bg-slate-200 text-slate-500 hover:text-slate-900'
          )}
          data-testid="login-theme-toggle"
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? (
            <Sun className="h-5 w-5" />
          ) : (
            <Moon className="h-5 w-5" />
          )}
        </Button>
      </div>

      <Card className={cn(
        "w-full max-w-md relative z-10 backdrop-blur-sm",
        isDark 
          ? "bg-slate-900/80 border-slate-800" 
          : "bg-white border-slate-200 shadow-lg"
      )}>
        <CardHeader className="text-center space-y-4">
          {/* Shield icon with glow effect */}
          <div className={cn(
            "mx-auto w-16 h-16 rounded-full flex items-center justify-center ring-1",
            isDark 
              ? "bg-gradient-to-br from-amber-500/20 to-orange-600/20 ring-amber-500/30"
              : "bg-gradient-to-br from-amber-100 to-orange-100 ring-amber-300"
          )}>
            <Shield className="w-8 h-8 text-amber-500" />
          </div>
          
          <div>
            <CardTitle className={cn(
              "text-2xl font-bold",
              isDark ? "text-white" : "text-slate-900"
            )}>
              AI Governance
            </CardTitle>
            <CardDescription className={isDark ? "text-slate-400 mt-1" : "text-slate-500 mt-1"}>
              Governor Control Plane (Lockene Internal Only)
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          {/* Security notice */}
          <div className={cn(
            "mb-6 p-3 rounded-lg border",
            isDark 
              ? "bg-amber-500/10 border-amber-500/20"
              : "bg-amber-50 border-amber-200"
          )}>
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className={cn(
                "text-xs",
                isDark ? "text-amber-200/80" : "text-amber-700"
              )}>
                This is a restricted access point. All access attempts are logged and monitored.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {sessionExpired && (
              <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
                <Clock className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700 dark:text-amber-400">
                  Your session has expired. Please log in again to continue.
                </AlertDescription>
              </Alert>
            )}
            
            {error && (
              <div className={cn(
                "p-3 rounded-lg border text-sm",
                isDark 
                  ? "bg-red-500/10 border-red-500/20 text-red-400"
                  : "bg-red-50 border-red-200 text-red-600"
              )}>
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className={isDark ? "text-slate-300" : "text-slate-700"}>
                Administrator Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@organization.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={cn(
                  isDark 
                    ? "bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20"
                    : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-amber-500 focus:ring-amber-500/20"
                )}
                data-testid="superadmin-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className={isDark ? "text-slate-300" : "text-slate-700"}>
                Access Key
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={cn(
                    "pr-10",
                    isDark 
                      ? "bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20"
                      : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-amber-500 focus:ring-amber-500/20"
                  )}
                  data-testid="superadmin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={cn(
                    "absolute right-3 top-1/2 -translate-y-1/2",
                    isDark ? "text-slate-500 hover:text-slate-300" : "text-slate-400 hover:text-slate-600"
                  )}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-medium"
              data-testid="superadmin-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Authenticating...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Access Control Plane
                </span>
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className={cn(
            "mt-6 pt-4 border-t",
            isDark ? "border-slate-800" : "border-slate-200"
          )}>
            <p className={cn(
              "text-xs text-center",
              isDark ? "text-slate-500" : "text-slate-400"
            )}>
              Lockene Enterprise AI Governance System
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
